import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  AccordionWrapper,
  AccordionHeader,
  AccordionButton,
  AccordionContent,
  Date as DateField,
  Title,
  Link,
  Arrow,
  Space,
  Information,
  Warning,
  ButtonsWrap,
} from './OlympiadDetailsStyled';

import Tooltip, { TooltipPlacement } from 'features/shared/Tooltip';
import starurl from 'assets/images/icons/star.svg';
import { Olympiad, OlympiadStatus } from 'features/olympiads/model';
import urlJoin from 'url-join';
import { LogClick, useClickStreamContext } from 'features/shared/ClickStream';
import { Text, Button } from '@gr-monorepo/common/ui';
import DownloadReportButton from './DownloadReportButton';

const regProtocol = /((http[s]?:)?\/\/)?/;
const regHttp = /^https?:\/\//i;

const useFixLink = (url?: string) => {
  return useMemo(() => {
    if (!url) return undefined;

    const getOlympiadLink = (sourceUrl: string) => {
      try {
        const isURLWithHttpSchema = regHttp.test(sourceUrl);
        const recreatedURLWithSchema = `https://${sourceUrl.replace(regProtocol, '')}`;
        const urlObject = new URL(isURLWithHttpSchema ? sourceUrl : recreatedURLWithSchema);
        urlObject.searchParams.set('from', 'cbm');
        const title = urlObject.hostname;
        const link = urlObject.href;

        return { title, link };
      } catch {
        return undefined;
      }
    };
    return getOlympiadLink(url);
  }, [url]);
};

const useHeightContentSetter = (contentRef: React.RefObject<HTMLDivElement>, collapse: boolean) => {
  useEffect(() => {
    if (!contentRef.current) return;
    if (collapse) {
      contentRef.current.style.height = '0px';
      contentRef.current.style.marginBottom = '0px';
    } else {
      contentRef.current.style.height = `${contentRef.current.scrollHeight}px`;
      contentRef.current.style.marginBottom = '16px';
    }
  }, [collapse]);
};

const OlympiadDetailsUi = (
  props: React.PropsWithChildren<{
    status: OlympiadStatus;
    pendingReport?: boolean;
    onDownloadReport?: () => void;
    showRating: boolean;
    data: Olympiad;
    withoutParticipants: boolean;
  }>,
) => {
  const { logPageEvent } = useClickStreamContext();

  const { name, url, dates, description, icon, attachments } = props.data;
  const { status, pendingReport, onDownloadReport } = props;
  const [collapse, setCollapse] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const isFuture = useMemo(() => {
    return status === OlympiadStatus.future;
  }, [status]);

  useHeightContentSetter(contentRef, collapse);
  const olympiadLink = useFixLink(url);

  return (
    <AccordionWrapper collapse={collapse} data-qa-id="olympiads_block">
      <DateField dangerouslySetInnerHTML={{ __html: dates || '' }}></DateField>
      <AccordionHeader>
        <LogClick
          eventType="olymp_сollapse_details"
          eventInfo={`Олимпиада: ${collapse ? 'Показать детализацию олимпиады' : 'Скрыть детализацию олимпиады'}`}
          cd1={props.data.id}
        >
          <AccordionButton onClick={() => setCollapse(!collapse)}>
            <Title>
              {description ? (
                <Tooltip
                  title={description}
                  arrow
                  placement={TooltipPlacement.TopStart}
                  minWidth={55}
                  maxWidth={300}
                  onShow={() => {
                    logPageEvent?.({
                      eventType: 'olymp_show_tooltip_olympiads_description',
                      eventInfo: 'Олимпиада: Показ тултипа "Описание олимпиад под звездочкой"',
                      cd1: props.data.id,
                    });
                  }}
                >
                  <Information style={{ backgroundImage: `url(${icon ? icon : starurl})` }} />
                </Tooltip>
              ) : (
                icon && <Information style={{ backgroundImage: 'url(' + icon + ')' }} />
              )}
              {name}
              {!isFuture && (
                <>
                  &nbsp;
                  <Arrow />
                </>
              )}
            </Title>
          </AccordionButton>
        </LogClick>
        <Space />
        {!isFuture && onDownloadReport && !props.showRating ? (
          <DownloadReportButton
            pendingReport={pendingReport}
            onDownloadReport={onDownloadReport}
            olympiadId={props.data.id}
            disabled={props.withoutParticipants}
          />
        ) : null}
      </AccordionHeader>
      {!isFuture && (
        <AccordionContent ref={contentRef}>
          {olympiadLink ? (
            <LogClick
              eventType="olymp_go_to_link"
              eventInfo="Олимпиада: Клик по ссылке на страницу олимпиады"
              cd1={props.data.id}
            >
              <Link href={olympiadLink.link} target={'_blank'}>
                {olympiadLink.title}
              </Link>
            </LogClick>
          ) : (
            <Link>&nbsp;</Link>
          )}
          {props.children}
        </AccordionContent>
      )}

      {attachments instanceof Array && attachments.length ? (
        <>
          <Text Grey style={{ margin: '8px 0 16px' }}>
            Пригласите учителей и учеников из вашего субъекта принять участие в олимпиаде. Скачайте инструкцию
            и информационное письмо и разошлите их по школам
          </Text>
          <ButtonsWrap>
            {attachments.map((el) => (
              <LogClick
                eventType="olymp_click_download_attachments"
                eventInfo="Олимпиада: Клик по ссылке в прикрепленных"
                cd1={props.data.id}
              >
                <Button color={el.type as 'blue' | 'azure'} as="a" href={el.link} target={'_blank'}>
                  {el.title}
                </Button>
              </LogClick>
            ))}
          </ButtonsWrap>
        </>
      ) : null}
    </AccordionWrapper>
  );
};

export default OlympiadDetailsUi;
